
import React, { createContext, useContext, ReactNode } from 'react';
import { useTwilioCall } from '@/hooks/useTwilioCall';
import { useTwilioTranscription, TranscriptMessage } from '@/hooks/useTwilioTranscription';
import { useAuth } from '@/context/AuthContext';

interface TwilioContextType {
  // Call state
  callInProgress: boolean;
  callStatus: string;
  callStartTime: Date | null;
  callSid: string | null;
  isMuted: boolean;
  formattedDuration: string;
  
  // Contact ownership state
  activeContactId: number | null;
  activeContactName: string | null;
  
  // Device state
  isInitializingDevices: boolean;
  micPermission: PermissionState | null;
  
  // Transcription state
  transcript: TranscriptMessage[];
  isListening: boolean;
  isSupported: boolean;
  backendConnected: boolean;
  
  // Actions - Updated to match actual return types
  makeCall: (phoneNumber: string, contactId?: number, contactName?: string, leadId?: number) => Promise<boolean>;
  endCall: () => Promise<boolean>;
  toggleMute: (muted: boolean) => Promise<boolean>;
  sendDTMF: (digit: string) => Promise<boolean>;
  requestPermissions: () => Promise<boolean>;
  initializeDevice: () => Promise<any>;
  dismissCall: () => void;
  
  // Transcription actions
  startTranscription: () => Promise<void>;
  stopTranscription: () => void;
  clearTranscript: () => void;
  downloadTranscript: () => void;
}

const TwilioContext = createContext<TwilioContextType | undefined>(undefined);

interface TwilioProviderProps {
  children: ReactNode;
}

export const TwilioProvider: React.FC<TwilioProviderProps> = ({ children }) => {
  // Only initialize Twilio if user is authenticated
  const { isAuthenticated } = useAuth();
  
  const twilioState = useTwilioCall();
  const transcriptionState = useTwilioTranscription(
    twilioState.callInProgress, 
    twilioState.callSid,
    undefined, // localPhoneNumber - TODO: get from call state
    undefined  // remotePhoneNumber - TODO: get from call state
  );
  
  const combinedState = {
    ...twilioState,
    ...transcriptionState,
    // Ensure contact ownership fields are included
    activeContactId: twilioState.activeContactId,
    activeContactName: twilioState.activeContactName
  };

  return (
    <TwilioContext.Provider value={combinedState}>
      {children}
    </TwilioContext.Provider>
  );
};

export const useTwilioContext = (): TwilioContextType => {
  const context = useContext(TwilioContext);
  if (context === undefined) {
    throw new Error('useTwilioContext must be used within a TwilioProvider');
  }
  return context;
};
